import type { AxiosInstance } from 'axios';
import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse, PaginatedResponse } from '../../types/api';
import type { FastchipIccidBatchMovementSourceForBypass, FastchipClusterStockBalanceResponse } from '../../types/dtos';
import { TrackingInfoDto } from '../../types/tracking';
import { withRequestTimeout } from '../utils/apiErrorUtils';

async function requestSuggestedSaleToken(
  api: AxiosInstance,
  clientId: string,
  options?: { deliveryCidade?: string; deliveryEstado?: string }
): Promise<string> {
  const queryParams = new URLSearchParams();
  queryParams.append('clientId', clientId);
  if (options?.deliveryCidade) queryParams.append('deliveryCidade', options.deliveryCidade);
  if (options?.deliveryEstado) queryParams.append('deliveryEstado', options.deliveryEstado);
  const response = await api.get<{ token?: string | null }>(
    `/contracts/suggested-sale-token?${queryParams.toString()}`
  );
  const token = response.data?.token;
  return typeof token === 'string' ? token : '';
}

// Enum para status de contrato (deve corresponder ao backend)
export enum EContractStatus {
  // Status básicos existentes
  Criado = 0,
  Assinado = 1,
  EmAnalise = 2,
  Ativo = 3,
  Suspenso = 4,
  Cancelado = 5,
  Finalizado = 6,
  
  // Status intermediários do Excel
  CVP = 10,
  EmAnaliseBackOffice = 11,
  AguardandoCI = 12,
  Qualidade = 13,
  AguardandoFIDEL = 14,
  AguardandoCorrecaoConsultor = 15,
  ValidandoDocumentacao = 16,
  Input = 17,
  AguardandoAnaliseAntiFraude = 18,
  AnaliseSupervisor = 19,
  ProcedimentoNAC = 20,
  AguardandoDocumentacao = 21,
  AguardandoEmissaoNF = 22,
  AguardandoEntrega = 23,
  AreaDeAtuacao = 24,
  ReanaliseCredito = 25,
  AguardandoAnaliseGerenteVendas = 26,
  ProcedimentoTrader = 27,
  ReanaliseADM = 28,
  AguardandoProcedimentoADM = 29,
  InsucessoEntrega = 30,
  
  // Status finais específicos
  ProvisionadoAguardandoEntrega = 31,
  ProcessoAtivacaoPortabilidades = 32,
  ErroPortabilidade = 33,
  
  // Status de aguardando assinatura
  AguardandoAssinatura = 34,
  
  // Status CQV - Decisão final da auditoria
  AprovadoCQV = 35,
  RecusadoCQV = 36,
  
  // Status de envio
  Enviado = 37,
  
  // Status de não assinado
  NaoAssinado = 38,
  
  // Status VTME
  VtmeCriado = 39,

  RecusadoQualidade = 40,
  RecusadoCVP = 41,
  TrocaDeCarteira = 42,
  TrocaDeCarteiraSolicitada = 62,
  VerificandoCarteira = 63,

  // Status VTME espelhados
  Insercao = 43,
  AnaliseHolding = 44,
  ProcessoDeAtivacao = 45,
  AguardandoAnaliseCreditoGerenteBusiness = 46,
  AguardandoAceiteDigital = 47,
  AguardandoAprovacaoCustomizacao = 48,
  AguardandoGerarP2B = 49,
  ContestacaoTim = 50,
  Cqv2 = 51,
  AprovacaoCodigo02 = 52,
  BateVolta = 53,
  SuporteHdc = 54,
  CanceladoVtme = 55,
  Reprovado = 56,
  ReprovadoCqv = 57,
  ReprovadoPorCredito = 58,
  ReprovadoQualidade = 59,
  RestricaoCreditoTim = 60,
  TrocaDeCarteiraNaoAutorizada = 61,
  RestricaoCreditoPendenciasMercado = 64,
  Rascunho = 65,

  // Status Qualidade / CCV (modal de linhas)
  QualidadeOk = 66,
  AprovadoCCV = 67,
  RecusadoCCV = 68,

  /** Pedido anterior a 22/04/2026. Sem e-mail, VTME, notificação ou Hangfire. */
  PedidoAntigo = 69,
}

// DTOs específicos para Lista de Contratos
export interface UserInfoDto {
  id: string;
  nome: string;
  email: string;
  username: string;
  /** Login/usuário VTME (cadastro do consultor em GettelApi `User.UsuarioVtme`). */
  usuarioVtme?: string | null;
}

export interface ContractListDto {
  id: string;
  clientRazaoSocial: string;
  clientCnpj: string;
  clientNomeFantasia: string;
  clientClassification?: string | null;
  clientInternalNotes?: string | null;
  clientEstado?: string;
  tipoContratacao: string;
  status: EContractStatus;
  dataAssinatura: string;
  dataVenda: string;
  valorTotalMensal: number;
  administratorNome: string;
  administratorCpf: string;
  administratorEmail: string;
  totalLinhas: number;
  createdAt: string;
  updatedAt: string;
  porteCliente: string;
  groupId?: string | null;
  contractType?: string | null;
  hasGroupedContracts?: boolean;
  fastchip?: boolean;
  fastchipEnviadoEm?: string | null;
  user?: UserInfoDto;
  createdByUser?: UserInfoDto;
  trackingCode?: string | null;
  temPortabilidade?: boolean;
  /** Alguma linha ativa marcada como troca de linha (suspensão manual). */
  temTrocaLinha?: boolean;
  /** Contrato criado com bypass de IE inapta. Badge "BYPASS IE" na listagem. */
  ieBypassedAtCreation?: boolean;
  /** Snapshot de IE ativa do cliente. false = IE inapta → badge "IE INAPTA" (se não-MEI). */
  clientHasActiveInscricaoEstadual?: boolean | null;
  /** Natureza jurídica — pra suprimir badge de IE inapta em MEI/individual. */
  clientNaturezaJuridica?: string | null;
  /** Resumo do último envio/tentativa DocuSign (RPA). */
  latestDocusignSend?: LatestDocusignSendSummaryDto | null;
  /** Token de cluster / pedido (ex.: TPCL, HB). */
  token?: string | null;
  /** Observações internas do contrato (uso exclusivo interno). */
  internalNotes?: string | null;
  /** Linha(s) com aparelho de catálogo (VF 24x), exceto WTTX. */
  temAparelhoCatalogo?: boolean;
  /** Contrato Fastchip com ao menos uma linha e-SIM. */
  temEsim?: boolean;
  /** Analista CQV dona do grupo no rodízio VTME. */
  vtmeAssignedName?: string | null;
  vtmeAssignedEmail?: string | null;
  resumoAparelhos?: ContractListHandsetSummaryItemDto[];
}

export interface ContractListHandsetSummaryItemDto {
  nome: string;
  quantidade: number;
  valorMensal: number;
  valorTotal: number;
}

export interface LatestDocusignSendSummaryDto {
  historyId: string;
  /** API envia string ("Success"); após normalização no front vira número do enum. */
  status: number | string;
  statusText: string;
  isProcessing: boolean;
  requestedAt: string;
  finishedAt?: string | null;
  recipientContactType: number;
  recipientContactTypeText: string;
  recipientName: string;
  recipientPhone: string;
  recipientEmail?: string | null;
  docusignAccountEmail?: string | null;
  docusignContractLink?: string | null;
  requestedByUserName?: string | null;
}

export interface ContractDocusignSendHistoryDto {
  id: string;
  contractId: string;
  contractGroupId?: string | null;
  docusignAccountEmail?: string | null;
  docusignEnvelopeId?: string | null;
  docusignContractLink?: string | null;
  requestedByUserId: string;
  requestedByUserName?: string | null;
  requestedByUserEmail?: string | null;
  requestedAt: string;
  recipientContactType: number;
  recipientContactTypeText: string;
  recipientName: string;
  recipientPhone: string;
  recipientEmail?: string | null;
  sentViaWhatsApp: boolean;
  sentViaEmail: boolean;
  isProcessing: boolean;
  status: number | string;
  statusText: string;
  finishedAt?: string | null;
  errorMessage?: string | null;
}

export interface SendContractToDocusignRequest {
  recipientEmail?: string;
  recipientName?: string;
  recipientPhone?: string;
  recipientContactType?: string;
}

export interface CancelDocusignProcessingRequest {
  contractIds?: string[];
}

export interface DocusignMemberSendHistoryDto {
  contractId: string;
  docusignSendHistoryId: string;
}

export interface SendGroupToDocusignResponse {
  success: boolean;
  message: string;
  groupId: string;
  leaderContractId: string;
  leaderHistoryId: string;
  memberSendHistories: DocusignMemberSendHistoryDto[];
}

export interface DocusignSendProgressUpdateDto {
  contractId: string;
  stage: string;
  progress: number;
  message: string;
  status: 'processing' | 'success' | 'error';
  timestamp?: string;
  preserveProgress?: boolean;
  resetProgress?: boolean;
  isQueued?: boolean;
  queuePosition?: number;
  queueTotal?: number;
}

export interface DocusignSendProgressResponseDto {
  contractId: string;
  currentProgress: DocusignSendProgressUpdateDto | null;
  progress?: DocusignSendProgressUpdateDto[];
}

export interface ContractListPagedResponseDto {
  items: ContractListDto[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
}

// DTO para contratos pendentes do Seller
export interface PendingSellerContractDto {
  id: string;
  clientCnpj: string;
  clientName: string;
  tipoContratacao: string;
  createdAt: string;
  valorTotalMensal: number;
  userId: string;        // ID do operador que criou
  userName: string;      // Nome do operador
  userEmail?: string;    // Email do operador (opcional)
}

// DTO para histórico de status de contratos
export interface ContractStatusHistoryDto {
  id: string;
  groupId?: string | null;
  status: number;
  statusText: string;
  previousStatus: number | null;
  previousStatusText: string | null;
  changedAt: string;
  changedByUserName: string | null;
  changedByUserEmail: string | null;
  changeReason: string | null;
}

export interface CarteiraAutomacaoUltimoResultadoDto {
  found: boolean;
  jobId?: string | null;
  jobStatus?: string | null;
  createdAt?: string | null;
  resultado?: string | null;
  statusChanged?: boolean;
  bloqueado?: boolean;
  bloqueadoCooldown?: boolean;
  diasRestantesCooldown?: number | null;
  cooldownAte?: string | null;
  solicitacaoStatus?: string | null;
  whatsappJaEnviado?: boolean;
  mensagemEnviada?: boolean;
  whatsappMensagemAlerta?: string | null;
  usuarioMensagemAlerta?: string | null;
  error?: string | null;
}

export interface CarteiraVerificacaoResponseDto {
  iniciada?: boolean;
  jobId?: string | null;
  erro?: string | null;
}

export interface UpdateContractStatusResultDto {
  contractId?: string;
  status?: string;
  requestedStatus?: string;
  carteiraVerificacao?: CarteiraVerificacaoResponseDto | null;
}

export interface CqvEmTrocaSolicitadaResultDto {
  success?: boolean;
  atcSynced?: boolean;
  finalStatus?: number;
  errorCode?: string | null;
  message?: string | null;
  contractsUpdated?: string[];
}

export interface ReconsultarCarteiraVtmeResultDto {
  success: boolean;
  resultado?: string | null;
  solicitacaoEncerrada?: boolean;
  solicitacaoStatus?: string | null;
  mensagemUsuario?: string | null;
  targetStatus?: number | null;
  carteiraAtual?: string | null;
  jobId?: string | null;
  error?: string | null;
}

export interface TrocaCarteiraSolicitacaoDto {
  found: boolean;
  cnpj?: string | null;
  podeSolicitarNovamente?: boolean;
  bloqueadoCooldown?: boolean;
  aguardandoRetornoWhatsapp?: boolean;
  diasRestantesCooldown?: number | null;
  cooldownAte?: string | null;
  mensagemUsuario?: string | null;
  solicitacaoStatus?: string | null;
  solicitacaoEnviadaEm?: string | null;
}

export interface ContractAuditLogDto {
  id: string;
  action: string;
  changedFields: string[];
  oldValues: string | null;
  newValues: string | null;
  timestamp: string;
  userName: string | null;
  userEmail: string | null;
}

export interface ContractLineAuditLogDto {
  id: string;
  contractId: string;
  contractLineId?: string | null;
  action: string;
  changedFields: string[];
  oldValues: string | null;
  newValues: string | null;
  changedAt: string;
  changedByUserName: string | null;
  changedByUserEmail: string | null;
}

export interface ContractLineAuditLogBucketDto {
  contractId: string;
  contractType?: string | null;
  tipoContratacao?: string | null;
  contractIsDeleted: boolean;
  logs: ContractLineAuditLogDto[];
}

export interface GroupContractLineAuditLogResponseDto {
  groupId: string;
  contracts: ContractLineAuditLogBucketDto[];
}

// DTOs específicos para Contratos - Nova estrutura de filtros
export interface ContractFiltersDto {
  // Paginação
  pageNumber?: number;
  pageSize?: number;
  
  // Ordenação
  orderBy?: string;
  isDescending?: boolean;
  
  // Filtros por Cliente
  clientId?: string;
  clientCnpj?: string;
  clientNomeFantasia?: string;
  clientRazaoSocial?: string;
  clientEstado?: string;
  clientCidade?: string;
  clientSearch?: string;
  
  // Filtros por Usuário
  userId?: string;
  userName?: string;
  
  // Filtros por Contrato
  status?: EContractStatus;
  tipoContratacao?: string;
  token?: string;
  /** Cluster efectivo por localização (multi-select, OR). */
  effectiveClusters?: string[];
  fastchip?: boolean;
  numeroP2B?: string;
  contractId?: string;
  iccid?: string;
  temTrocaLinha?: boolean;
  temPendenciaSuspensaoLinhas?: boolean;
  /** Contrato com linha ativa e aparelho de catálogo (VF 24x), exceto WTTX. */
  temAparelhoCatalogo?: boolean;
  
  // Filtros por Data
  dataAssinaturaDe?: Date;
  dataAssinaturaAte?: Date;
  dataVendaDe?: Date;
  dataVendaAte?: Date;
  createdAtDe?: Date;
  createdAtAte?: Date;
  
  // Filtros por Valores
  valorTotalMensalDe?: number;
  valorTotalMensalAte?: number;
  comprometimentoMinimo?: number;
  
  // Filtros por Endereço de Entrega
  enderecoCep?: string;
  enderecoCidade?: string;
  enderecoEstado?: string;
  
  // Filtros por Administrador
  administratorNome?: string;
  administratorCpf?: string;
  administratorEmail?: string;
  
  // Filtros por Representante
  representativeNome?: string;
  representativeCpf?: string;
  
  // Filtros por Testemunha
  witnessNome?: string;
  witnessCpf?: string;
  
  // Filtros por Linha do Contrato
  linhaNegociacao?: string;
  linhaDdd?: string;
  linhaTipoVenda?: string;
  planId?: string;
  planName?: string;
  
  // Filtros por Oferta
  ofertaNome?: string;
  ofertaValorDe?: number;
  ofertaValorAte?: number;
  
  // Filtros de Auditoria
  isDeleted?: boolean;
  updatedBy?: string;
  deletedBy?: string;
  
  // Busca geral
  searchTerm?: string;
}

export interface ContractPagedResponseDto {
  items: ContractResponseDto[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  appliedFilters?: ContractFiltersDto;
}

// DTOs de resposta baseados no backend
export interface ContractResponseDto {
  id: string;
  clientId: string;
  userId: string;
  createdByUserId?: string;
  status?: EContractStatus;
  clientCnpj: string;
  clientPhoneNumber: string;
  tipoContratacao: string;
  diaVencimento: number;
  dataAssinatura?: Date; // Opcional
  valorTotalMensal?: number;
  dataVenda?: Date; // Opcional
  numeroP2B?: string;
  comprometimentoMinimo?: number;
  token?: string;
  fastchip: boolean;
  tipoFatura: string;
  origemVenda: string;
  /** Iniciais do cluster Senior (Token / Origem da venda CQV) — calculado na API. */
  clusterIniciais?: string;
  envioFatura: string;
  /** Marca contratos criados ignorando o bloqueio de IE inapta (Aditivo + Fastchip=false). Frontend exibe badge "BYPASS IE". */
  ieBypassedAtCreation?: boolean;
  /** Justificativa textual informada pelo usuário ao acionar o bypass de IE — exibida em tooltip do badge. */
  ieBypassReason?: string | null;
  duplicateReason?: string | null;
  internalNotes?: string | null;
  groupId?: string | null;
  contractType?: string | null;
  trackingCode?: string | null;
  dataEnvio?: Date | null;
  radarPedido?: string;
  p2BPedido?: string;
  idPedido?: string;
  vtmeConfirmado?: boolean | null;
  dataConfirmacaoVtme?: Date | null;
  usuarioConfirmacaoVtmeId?: string | null;
  idPedidoContrato1?: string | null;
  idPedidoContrato2?: string | null;
  client: ClientSummaryDto;
  user: UserSummaryDto;
  createdByUser?: UserSummaryDto;
  deliveryAddress: ContractDeliveryAddressDto | null;
  administrator: ContractAdministratorDto | null;
  representatives: ContractRepresentativeDto[];
  authorizedReceivers: ContractAuthorizedReceiverDto[];
  witnesses: ContractWitnessDto[];
  linhas: ContractLineDto[];
  /** Indica se o usuário atual pode alterar linhas (sem bypass). */
  canEditContractLines?: boolean;
  /** false = fluxo 1056 (contratos novos); omitido/true = suspensão permitida (#553). */
  suspensaoPermitida?: boolean | null;
  createdAt: Date;
  updatedAt?: Date;
  updatedBy?: string;
  isDeleted: boolean;
  deletedBy?: string;
  deletedAt?: Date;
  listItem?: ContractListDto | null;
}

export interface ClientSummaryDto {
  id: string;
  tipoCliente: string;
  segmento?: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia?: string;
  estado: string;
  /**
   * Snapshot do status de Inscrição Estadual ativa do último sync com a Receita.
   * - undefined/null = nunca sincronizado (sem info confiável → não bloqueia, não mostra badge)
   * - false = IE inapta/baixada/sem registro no UF → mostrar badge "IE INAPTA" (apenas se não-MEI)
   * - true = IE ativa no UF
   */
  hasActiveInscricaoEstadual?: boolean | null;
  /** Data UTC do último sync com Receita. Se > 45d, mostrar aviso de "dados desatualizados". */
  lastCnpjSync?: string | null;
  /** Natureza jurídica — usada pra detectar MEI/individual e suprimir badge de IE inapta. */
  naturezaJuridica?: string | null;
}

export interface UserSummaryDto {
  id: string;
  nome: string;
  email: string;
  username: string;
  usuarioVtme?: string | null;
}

export interface ContractLineDetailsModalDto {
  id: string;
  status?: EContractStatus;
  tipoContratacao: string;
  contractType?: string | null;
  valorTotalMensal?: number;
  fastchip: boolean;
  token?: string | null;
  fastchipEnviadoEm?: string | null;
  vtmeConfirmado?: boolean | null;
  dataConfirmacaoVtme?: Date | null;
  idPedidoContrato1?: string | null;
  idPedidoContrato2?: string | null;
  clientRazaoSocial?: string;
  clientCnpj?: string;
  clientId?: string;
  clientEstado?: string | null;
  clientCidade?: string | null;
  clientClassification?: string | null;
  clientInternalNotes?: string | null;
  internalNotes?: string | null;

  userId?: string;
  user?: UserInfoDto;

  /** Snapshot de IE ativa do cliente. false = inapta/baixada → badge "IE INAPTA" no header do modal. */
  clientHasActiveInscricaoEstadual?: boolean | null;
  /** Última sync com Receita; se > 45d, sinalizar "dados podem estar desatualizados". */
  clientLastCnpjSync?: string | null;
  /** Natureza jurídica — pra suprimir alerta em MEI/individual. */
  clientNaturezaJuridica?: string | null;

  /** Marca contratos criados ignorando o bloqueio de IE inapta. Renderizar badge "BYPASS IE" no modal. */
  ieBypassedAtCreation?: boolean;
  /** Justificativa do bypass de IE, exibida em tooltip do badge. */
  ieBypassReason?: string | null;

  administrator?: ContractAdministratorDto;
  representatives: ContractRepresentativeSummaryDto[];
  /** Recebedores autorizados cadastrados na criação do contrato */
  authorizedReceivers?: ContractAuthorizedReceiverSummaryDto[];
  /** Endereco de entrega informado na criacao (necessario para o card no modal de detalhes) */
  deliveryAddress?: ContractDeliveryAddressDto;
  /** Incluído quando o endpoint retorna cabeçalho + linhas em uma única chamada */
  linhas?: ContractLineDto[];

  /** Elegibilidade para edição de linhas (sem bypass do usuário). */
  canEditContractLines?: boolean;

  /** JSON com blocos do roteiro WhatsApp CQV já copiados (persistido na API). */
  cqvWhatsappCopyStateJson?: string | null;

  /** Analista CQV dona do grupo no rodízio VTME. */
  vtmeAssignedName?: string | null;
  vtmeAssignedEmail?: string | null;
}

export interface ContractRepresentativeSummaryDto {
  id?: string;
  nome: string;
  cpf: string;
  rg?: string;
  email?: string;
  telefone?: string;
}

export interface ContractAuthorizedReceiverSummaryDto {
  id: string;
  nome: string;
  qualificacao?: string;
  cpf: string;
  rg?: string;
}

// DTOs de composição
export interface ContractDeliveryAddressDto {
  id: string;
  contractId: string;
  cep: string;
  cidade: string;
  estado: string;
  endereco: string;
  numero: string;
  bairro: string;
  complemento?: string;
  referencia?: string;
  createdAt: Date;
  updatedAt?: Date;
  isDeleted: boolean;
}

export interface ContractAdministratorDto {
  id: string;
  contractId: string;
  nomeCompleto: string;
  cpf: string;
  rg: string;
  email?: string;
  telefone1?: string;
  telefone2?: string;
  dataNascimento: Date;
  createdAt: Date;
  updatedAt?: Date;
  isDeleted: boolean;
}

export interface ContractRepresentativeDto {
  id: string;
  contractId: string;
  nome: string;
  cpf: string;
  rg?: string;
  telefone?: string;
  email?: string;
  createdAt: Date;
  updatedAt?: Date;
  isDeleted: boolean;
}

export interface ContractAuthorizedReceiverDto {
  id: string;
  contractId: string;
  nome: string;
  qualificacao?: string;
  dataNascimento?: Date;
  cpf: string;
  rg?: string;
  email?: string;
  telefone?: string;
  celular?: string;
  nomePai?: string;
  nomeMae?: string;
  naturalidade?: string;
  createdAt: Date;
  updatedAt?: Date;
  isDeleted: boolean;
}

export interface ContractWitnessDto {
  id: string;
  contractId: string;
  nome: string;
  cpf: string;
  rg?: string;
  createdAt: Date;
  updatedAt?: Date;
  isDeleted: boolean;
}

export interface ContractLineDto {
  id: string;
  contractId: string;
  negociacao?: string;
  ddd?: string;
  numero?: string; // Número de telefone para renegociação
  chipCcid?: string; // Código de campanha do chip (ICCID)
  tipoVenda?: string;
  planId?: string;
  plano?: string;
  planName?: string;
  aparelho?: string;
  handsetDeviceId?: string;
  aparelhoValorTotal?: number;
  aparelhoValorMensal?: number;
  valorChip?: number;
  valorBasePlano?: number;
  valorServicos?: number;
  valorOfertas?: number;
  valorMensal: number;
  valorTotalMensal?: number;
  quantidade: number;
  ofertas: ContractOfferDto[];
  createdAt: Date;
  updatedAt?: Date;
  updatedBy?: string;
  isDeleted: boolean;
  deletedBy?: string;
  deletedAt?: Date;
  // Campos de suspensão
  excluirDoContrato?: boolean;
  requerSuspensao?: boolean;
  /** Já teve a 1ª suspensão (ou re-suspensão) registada — fatia 1 GCM usa para ocultar a ação de 1ª. */
  estaSuspensa?: boolean;
  trocaLinha?: boolean;
  meuTimLogin?: string;
  meuTimSenha?: string;
  protocolo?: string;
  seraUsadaPeloCliente?: boolean;
  isApto?: boolean | null;
  // Portabilidade
  ehPortabilidade?: boolean;
  portabilidadeNomeCompleto?: string;
  portabilidadeCpf?: string;
  portabilidadeOperadora?: string;
  portabilidadeLinha?: string;
  /** CNPJ do cedente, quando a portabilidade é entre CNPJs diferentes (cnpj-diferente). */
  portabilidadeCnpjCedente?: string;
  /** Razão social do cedente, exigida junto com o CNPJ do cedente. */
  portabilidadeRazaoSocialCedente?: string;
  /** Representantes legais do cedente (nome + CPF), exigidos em cnpj-diferente. */
  portabilidadeRepresentantes?: PortabilidadeRepresentanteDto[];
  /** e-SIM: chip virtual ativado via QR Code */
  isEsim?: boolean;
  /** false = linha em contrato sujeito à política 1056 (#553). */
  suspensaoPermitida?: boolean | null;
}

/** Representante legal do cedente (nome + CPF) para portabilidade cnpj-diferente. */
export interface PortabilidadeRepresentanteDto {
  nome: string;
  cpf: string;
}

export interface ContractOfferDto {
  id: string;
  contractLineId: string;
  nomeOferta: string;
  valorMensal: number;
  incluso: boolean;
  createdAt: Date;
  updatedAt?: Date;
  updatedBy?: string;
  isDeleted: boolean;
  deletedBy?: string;
  deletedAt?: Date;
}

export interface UpdateTimCredentialsRequestDto {
  meuTimLogin?: string;
  meuTimSenha?: string;
}

export interface LinhaAptaDto {
  linhaId: string;
  apto: boolean;
}

export interface ConfirmarVtmeRequest {
  idPedidoContrato1?: string | null;
  idPedidoContrato2?: string | null;
  linhasAptas: LinhaAptaDto[];
}

export interface UpdatePedidoIdsRequest {
  idPedidoContrato1?: string | null;
  idPedidoContrato2?: string | null;
}

// DTOs de criação - baseados nos DTOs do backend
export interface DraftSummaryDto {
  id: string;
  clientId: string;
  createdAt: string;
  createdByUserId: string;
  createdByUserName: string;
  lineCount: number;
  tipoContratacaoParcial?: string | null;
  valorTotalMensalParcial?: number | null;
}

export interface CreateContractRequestDto {
  clientId: string;
  userId: string;
  tipoContratacao?: string; // Opcional - pode ser "1º Venda", "Aditivo", "Renegociação" ou undefined para detecção automática
  diaVencimento: number;
  valorTotalMensal?: number; // Opcional - calculado automaticamente pelo backend
  numeroP2B?: string;
  comprometimentoMinimo?: number;
  token?: string;
  fastchip: boolean;
  internalNotes?: string;
  internalNotesOccurrence?: string;
  /**
   * Quando true, indica que o usuário confirmou prosseguir mesmo com IE inapta
   * (Aditivo + Fastchip=false + cliente sem IE ativa + não-MEI).
   * Backend persiste como bypass no contrato e dispara notificação ao criador + líder do time.
   */
  ignoreIeBlock?: boolean;
  /** Justificativa obrigatória (mín 10, máx 500 chars) quando ignoreIeBlock=true. */
  ieBypassReason?: string;
  tipoFatura: string;
  envioFatura: string;
  origemVenda: string;
  duplicateReason?: string; // Motivo para criação de contrato duplicado
  shouldSeparate?: boolean; // Se deve separar contratos Aditivo/Renegociação
  deliveryAddress: CreateContractDeliveryAddressDto;
  administrator: CreateContractAdministratorDto;
  representatives: CreateContractRepresentativeDto[];
  authorizedReceivers: CreateContractAuthorizedReceiverDto[];
  witnesses: CreateContractWitnessDto[];
  lines: CreateContractLineDto[];
}

export interface CreateContractDeliveryAddressDto {
  cep: string;
  cidade: string;
  estado: string;
  endereco: string;
  numero: string;
  bairro: string;
  complemento?: string;
  referencia?: string;
}

export interface CreateContractAdministratorDto {
  nomeCompleto: string;
  cpf: string;
  rg: string;
  email?: string;
  telefone1?: string;
  telefone2?: string;
  dataNascimento: Date;
}

export interface CreateContractRepresentativeDto {
  nome: string;
  cpf: string;
  rg?: string;
  telefone: string;
  email: string;
}

export interface CreateContractAuthorizedReceiverDto {
  nome: string;
  qualificacao?: string;
  dataNascimento?: Date;
  cpf: string;
  rg?: string;
  email?: string;
  telefone?: string;
  celular?: string;
  nomePai?: string;
  nomeMae?: string;
  naturalidade?: string;
}

export interface CreateContractWitnessDto {
  nome: string;
  cpf: string;
  rg?: string;
}

export interface CreateContractLineDto {
  negociacao?: string;
  ddd?: string;
  numero?: string;
  chipCcid?: string;
  tipoVenda?: string;
  planId?: string;
  aparelho?: string;
  handsetDeviceId?: string;
  aparelhoValorTotal?: number;
  aparelhoValorMensal?: number;
  valorChip?: number;
  valorMensal: number;
  quantidade: number;
  seraUsadaPeloCliente?: boolean | null;
  ofertas: CreateContractOfferDto[];
  // Portabilidade
  ehPortabilidade?: boolean;
  portabilidadeNomeCompleto?: string;
  portabilidadeCpf?: string;
  portabilidadeOperadora?: string;
  portabilidadeLinha?: string;
  /** CNPJ do cedente, quando a portabilidade é entre CNPJs diferentes (cnpj-diferente). */
  portabilidadeCnpjCedente?: string;
  /** Razão social do cedente, exigida junto com o CNPJ do cedente. */
  portabilidadeRazaoSocialCedente?: string;
  /** Representantes legais do cedente (nome + CPF), exigidos em cnpj-diferente. */
  portabilidadeRepresentantes?: PortabilidadeRepresentanteDto[];
  /** e-SIM: chip virtual ativado via QR Code (Fastchip Aditivo). */
  isEsim?: boolean;
}

export interface CreateContractOfferDto {
  nomeOferta: string;
  valorMensal: number;
  incluso: boolean;
}

// DTOs de atualização
export interface UpdateContractRequestDto {
  updatedAt?: string;
  tipoContratacao?: string;
  diaVencimento?: number;
  dataAssinatura?: Date;
  valorTotalMensal?: number;
  dataVenda?: Date;
  numeroP2B?: string;
  comprometimentoMinimo?: number;
  token?: string;
  fastchip?: boolean;
  /** Bypass de IE (ver CreateContractRequestDto.ignoreIeBlock). Edição também passa por validação no backend. */
  ignoreIeBlock?: boolean;
  /** Justificativa do bypass — obrigatória quando ignoreIeBlock=true. */
  ieBypassReason?: string;
  tipoFatura?: string;
  envioFatura?: string;
  origemVenda?: string;
  deliveryAddress?: UpdateContractDeliveryAddressDto;
  administrator?: UpdateContractAdministratorDto;
  representatives?: UpdateContractRepresentativeDto[];
  authorizedReceivers?: UpdateContractAuthorizedReceiverDto[];
  witnesses?: UpdateContractWitnessDto[];
  lines?: UpdateContractLineDto[];
}

export interface PatchContractResultDto {
  contractId: string;
  valorTotalMensal?: number;
  totalLinhas: number;
  wasCancelled: boolean;
  wasDeleted: boolean;
  cancellationReason?: string | null;
}

export interface EnsureGroupForLineEditRequestDto {
  requiredNegotiations: string[];
}

export interface EnsureGroupForLineEditResponseDto {
  groupId: string;
  contracts: ContractResponseDto[];
  createdContractIds: string[];
}

export interface PatchContractRequestDto {
  updatedAt?: string;
  tipoContratacao?: string;
  diaVencimento?: number;
  valorTotalMensal?: number;
  numeroP2B?: string;
  comprometimentoMinimo?: number;
  token?: string;
  fastchip?: boolean;
  internalNotes?: string;
  tipoFatura?: string;
  envioFatura?: string;
  origemVenda?: string;
  status?: string;
  deliveryAddress?: UpdateContractDeliveryAddressDto;
  administrator?: UpdateContractAdministratorDto;
  representatives?: UpdateContractRepresentativeDto[];
  authorizedReceivers?: UpdateContractAuthorizedReceiverDto[];
  witnesses?: UpdateContractWitnessDto[];
  lines?: UpdateContractLineDto[];
}

export interface PatchCqvWhatsappCopyStateRequestDto {
  blocks: Record<string, boolean>;
}

export interface PatchCqvWhatsappCopyStateResponseDto {
  cqvWhatsappCopyStateJson?: string | null;
}

export interface UpdateContractDeliveryAddressDto {
  cep?: string;
  cidade?: string;
  estado?: string;
  endereco?: string;
  numero?: string;
  bairro?: string;
  complemento?: string;
  referencia?: string;
}

export interface UpdateContractAdministratorDto {
  nomeCompleto?: string;
  cpf?: string;
  rg?: string;
  email?: string;
  telefone1?: string;
  telefone2?: string;
  dataNascimento?: Date;
}

export interface UpdateContractRepresentativeDto {
  id?: string;
  nome?: string;
  cpf?: string;
  rg?: string;
  telefone: string;
  email: string;
}

export interface UpdateContractAuthorizedReceiverDto {
  id?: string;
  nome?: string;
  qualificacao?: string;
  dataNascimento?: Date;
  cpf?: string;
  rg?: string;
  email?: string;
  telefone?: string;
  celular?: string;
  nomePai?: string;
  nomeMae?: string;
  naturalidade?: string;
}

export interface UpdateContractWitnessDto {
  id?: string;
  nome?: string;
  cpf?: string;
  rg?: string;
}

export interface UpdateContractLineDto {
  id?: string;
  negociacao?: string;
  ddd?: string;
  numero?: string;
  chipCcid?: string;
  tipoVenda?: string;
  planId?: string;
  plano?: string;
  aparelho?: string;
  handsetDeviceId?: string;
  aparelhoValorTotal?: number;
  aparelhoValorMensal?: number;
  valorChip?: number;
  valorMensal?: number;
  quantidade?: number;
  excluirDoContrato?: boolean;
  requerSuspensao?: boolean;
  trocaLinha?: boolean;
  meuTimLogin?: string;
  meuTimSenha?: string;
  protocolo?: string;
  dataDesativacao?: string;
  seraUsadaPeloCliente?: boolean | null;
  ofertas?: UpdateContractOfferDto[];
  // Portabilidade
  ehPortabilidade?: boolean;
  portabilidadeNomeCompleto?: string;
  portabilidadeCpf?: string;
  portabilidadeOperadora?: string;
  portabilidadeLinha?: string;
  /** CNPJ do cedente, quando a portabilidade é entre CNPJs diferentes (cnpj-diferente). */
  portabilidadeCnpjCedente?: string;
  /** Razão social do cedente, exigida junto com o CNPJ do cedente. */
  portabilidadeRazaoSocialCedente?: string;
  /** Representantes legais do cedente (nome + CPF), exigidos em cnpj-diferente. */
  portabilidadeRepresentantes?: PortabilidadeRepresentanteDto[];
  isEsim?: boolean;
}

export interface UpdateContractOfferDto {
  nomeOferta?: string;
  valorMensal?: number;
  incluso?: boolean;
}

export class ContractService extends BaseApiService {
  /**
   * Busca contratos com paginação padrão
   */
  async getContractsPaged(pageNumber: number = 1, pageSize: number = 10): Promise<ContractPagedResponseDto> {
    const response = await this.api.get<ContractPagedResponseDto>(`/contracts?pageNumber=${pageNumber}&pageSize=${pageSize}`);
    return response.data;
  }

  /**
   * Balanço consolidado de estoque Fastchip por cluster (uma requisição leve para ContractsPage).
   */
  async getFastchipClusterStockBalance(): Promise<FastchipClusterStockBalanceResponse> {
    const response = await this.api.get<FastchipClusterStockBalanceResponse>('/contracts/fastchip-stock-balance');
    return response.data;
  }

  /**
   * Busca contratos com filtros avançados
   */
  async getContractsWithFilters(filters: ContractFiltersDto): Promise<ContractPagedResponseDto> {
    const queryParams = new URLSearchParams();

    // Paginação e Ordenação
    if (filters.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters.orderBy) queryParams.append('orderBy', filters.orderBy);
    if (filters.isDescending !== undefined) queryParams.append('isDescending', filters.isDescending.toString());

    // Filtros por Cliente
    if (filters.clientId) queryParams.append('clientId', filters.clientId);
    if (filters.clientCnpj) queryParams.append('clientCnpj', filters.clientCnpj);
    if (filters.clientNomeFantasia) queryParams.append('clientNomeFantasia', filters.clientNomeFantasia);
    if (filters.clientRazaoSocial) queryParams.append('clientRazaoSocial', filters.clientRazaoSocial);
    if (filters.clientEstado) queryParams.append('clientEstado', filters.clientEstado);
    if (filters.clientCidade) queryParams.append('clientCidade', filters.clientCidade);
    if (filters.clientSearch) queryParams.append('clientSearch', filters.clientSearch);

    // Filtros por Usuário
    if (filters.userId) queryParams.append('userId', filters.userId);
    if (filters.userName) queryParams.append('userName', filters.userName);

    // Filtros por Contrato
    if (filters.status !== undefined) queryParams.append('status', filters.status.toString());
    if (filters.tipoContratacao) queryParams.append('tipoContratacao', filters.tipoContratacao);
    if (filters.token) queryParams.append('token', filters.token);
    if (filters.fastchip !== undefined) queryParams.append('fastchip', filters.fastchip.toString());
    if (filters.numeroP2B) queryParams.append('numeroP2B', filters.numeroP2B);
    if (filters.contractId) queryParams.append('contractId', filters.contractId);
    if (filters.iccid) queryParams.append('iccid', filters.iccid);

    // Filtros por Data
    if (filters.dataAssinaturaDe) queryParams.append('dataAssinaturaDe', filters.dataAssinaturaDe.toISOString());
    if (filters.dataAssinaturaAte) queryParams.append('dataAssinaturaAte', filters.dataAssinaturaAte.toISOString());
    if (filters.dataVendaDe) queryParams.append('dataVendaDe', filters.dataVendaDe.toISOString());
    if (filters.dataVendaAte) queryParams.append('dataVendaAte', filters.dataVendaAte.toISOString());
    if (filters.createdAtDe) queryParams.append('createdAtDe', filters.createdAtDe.toISOString());
    if (filters.createdAtAte) queryParams.append('createdAtAte', filters.createdAtAte.toISOString());

    // Filtros por Valores
    if (filters.valorTotalMensalDe) queryParams.append('valorTotalMensalDe', filters.valorTotalMensalDe.toString());
    if (filters.valorTotalMensalAte) queryParams.append('valorTotalMensalAte', filters.valorTotalMensalAte.toString());
    if (filters.comprometimentoMinimo) queryParams.append('comprometimentoMinimo', filters.comprometimentoMinimo.toString());

    // Filtros por Endereço
    if (filters.enderecoCep) queryParams.append('enderecoCep', filters.enderecoCep);
    if (filters.enderecoCidade) queryParams.append('enderecoCidade', filters.enderecoCidade);
    if (filters.enderecoEstado) queryParams.append('enderecoEstado', filters.enderecoEstado);

    // Filtros por Administrador
    if (filters.administratorNome) queryParams.append('administratorNome', filters.administratorNome);
    if (filters.administratorCpf) queryParams.append('administratorCpf', filters.administratorCpf);
    if (filters.administratorEmail) queryParams.append('administratorEmail', filters.administratorEmail);

    // Filtros por Representante
    if (filters.representativeNome) queryParams.append('representativeNome', filters.representativeNome);
    if (filters.representativeCpf) queryParams.append('representativeCpf', filters.representativeCpf);

    // Filtros por Testemunha
    if (filters.witnessNome) queryParams.append('witnessNome', filters.witnessNome);
    if (filters.witnessCpf) queryParams.append('witnessCpf', filters.witnessCpf);

    // Filtros por Linha
    if (filters.linhaNegociacao) queryParams.append('linhaNegociacao', filters.linhaNegociacao);
    if (filters.linhaDdd) queryParams.append('linhaDdd', filters.linhaDdd);
    if (filters.linhaTipoVenda) queryParams.append('linhaTipoVenda', filters.linhaTipoVenda);
    if (filters.planId) queryParams.append('planId', filters.planId);
    if (filters.planName) queryParams.append('planName', filters.planName);

    // Filtros por Oferta
    if (filters.ofertaNome) queryParams.append('ofertaNome', filters.ofertaNome);
    if (filters.ofertaValorDe) queryParams.append('ofertaValorDe', filters.ofertaValorDe.toString());
    if (filters.ofertaValorAte) queryParams.append('ofertaValorAte', filters.ofertaValorAte.toString());

    // Filtros de Auditoria
    if (filters.isDeleted !== undefined) queryParams.append('isDeleted', filters.isDeleted.toString());
    if (filters.updatedBy) queryParams.append('updatedBy', filters.updatedBy);
    if (filters.deletedBy) queryParams.append('deletedBy', filters.deletedBy);

    // Busca geral
    if (filters.searchTerm) queryParams.append('searchTerm', filters.searchTerm);

    const response = await this.api.get<ContractPagedResponseDto>(`/contracts?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Busca lista de contratos otimizada para exibição em tabela.
   * `options.signal` permite cancelar a requisição (ex.: quando uma chamada mais recente a substitui).
   */
  async getListContracts(
    filters: ContractFiltersDto,
    options?: { signal?: AbortSignal; timeoutMs?: number }
  ): Promise<ContractListPagedResponseDto> {
    const queryParams = new URLSearchParams();

    // Paginação e Ordenação
    if (filters.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters.orderBy) queryParams.append('orderBy', filters.orderBy);
    if (filters.isDescending !== undefined) queryParams.append('isDescending', filters.isDescending.toString());

    // Filtros por Cliente
    if (filters.clientId) queryParams.append('clientId', filters.clientId);
    if (filters.clientCnpj) queryParams.append('clientCnpj', filters.clientCnpj);
    if (filters.clientNomeFantasia) queryParams.append('clientNomeFantasia', filters.clientNomeFantasia);
    if (filters.clientRazaoSocial) queryParams.append('clientRazaoSocial', filters.clientRazaoSocial);
    if (filters.clientEstado) queryParams.append('clientEstado', filters.clientEstado);
    if (filters.clientCidade) queryParams.append('clientCidade', filters.clientCidade);
    if (filters.clientSearch) queryParams.append('clientSearch', filters.clientSearch);

    // Filtros por Usuário
    if (filters.userId) queryParams.append('userId', filters.userId);
    if (filters.userName) queryParams.append('userName', filters.userName);

    // Filtros por Contrato
    if (filters.status !== undefined) queryParams.append('status', filters.status.toString());
    if (filters.tipoContratacao) queryParams.append('tipoContratacao', filters.tipoContratacao);
    if (filters.token) queryParams.append('token', filters.token);
    filters.effectiveClusters?.forEach((c) => {
      if (c?.trim()) queryParams.append('effectiveClusters', c.trim());
    });
    if (filters.fastchip !== undefined) queryParams.append('fastchip', filters.fastchip.toString());
    if (filters.numeroP2B) queryParams.append('numeroP2B', filters.numeroP2B);
    if (filters.contractId) queryParams.append('contractId', filters.contractId);
    if (filters.iccid) queryParams.append('iccid', filters.iccid);
    if (filters.temTrocaLinha !== undefined)
      queryParams.append('temTrocaLinha', filters.temTrocaLinha.toString());
    if (filters.temPendenciaSuspensaoLinhas !== undefined)
      queryParams.append('temPendenciaSuspensaoLinhas', filters.temPendenciaSuspensaoLinhas.toString());
    if (filters.temAparelhoCatalogo !== undefined)
      queryParams.append('temAparelhoCatalogo', filters.temAparelhoCatalogo.toString());

    // Filtros por Data
    if (filters.dataAssinaturaDe) queryParams.append('dataAssinaturaDe', filters.dataAssinaturaDe.toISOString());
    if (filters.dataAssinaturaAte) queryParams.append('dataAssinaturaAte', filters.dataAssinaturaAte.toISOString());
    if (filters.dataVendaDe) queryParams.append('dataVendaDe', filters.dataVendaDe.toISOString());
    if (filters.dataVendaAte) queryParams.append('dataVendaAte', filters.dataVendaAte.toISOString());
    if (filters.createdAtDe) queryParams.append('createdAtDe', filters.createdAtDe.toISOString());
    if (filters.createdAtAte) queryParams.append('createdAtAte', filters.createdAtAte.toISOString());

    // Filtros por Valores
    if (filters.valorTotalMensalDe !== undefined) queryParams.append('valorTotalMensalDe', filters.valorTotalMensalDe.toString());
    if (filters.valorTotalMensalAte !== undefined) queryParams.append('valorTotalMensalAte', filters.valorTotalMensalAte.toString());
    if (filters.comprometimentoMinimo !== undefined) queryParams.append('comprometimentoMinimo', filters.comprometimentoMinimo.toString());

    // Filtros por Endereço
    if (filters.enderecoCep) queryParams.append('enderecoCep', filters.enderecoCep);
    if (filters.enderecoCidade) queryParams.append('enderecoCidade', filters.enderecoCidade);
    if (filters.enderecoEstado) queryParams.append('enderecoEstado', filters.enderecoEstado);

    // Filtros por Administrador
    if (filters.administratorNome) queryParams.append('administratorNome', filters.administratorNome);
    if (filters.administratorCpf) queryParams.append('administratorCpf', filters.administratorCpf);
    if (filters.administratorEmail) queryParams.append('administratorEmail', filters.administratorEmail);

    // Filtros por Representante
    if (filters.representativeNome) queryParams.append('representativeNome', filters.representativeNome);
    if (filters.representativeCpf) queryParams.append('representativeCpf', filters.representativeCpf);

    // Filtros por Testemunha
    if (filters.witnessNome) queryParams.append('witnessNome', filters.witnessNome);
    if (filters.witnessCpf) queryParams.append('witnessCpf', filters.witnessCpf);

    // Filtros por Linha
    if (filters.linhaNegociacao) queryParams.append('linhaNegociacao', filters.linhaNegociacao);
    if (filters.linhaDdd) queryParams.append('linhaDdd', filters.linhaDdd);
    if (filters.linhaTipoVenda) queryParams.append('linhaTipoVenda', filters.linhaTipoVenda);

    // Filtros por Plano
    if (filters.planId) queryParams.append('planId', filters.planId);
    if (filters.planName) queryParams.append('planName', filters.planName);

    // Filtros por Oferta
    if (filters.ofertaNome) queryParams.append('ofertaNome', filters.ofertaNome);
    if (filters.ofertaValorDe !== undefined) queryParams.append('ofertaValorDe', filters.ofertaValorDe.toString());
    if (filters.ofertaValorAte !== undefined) queryParams.append('ofertaValorAte', filters.ofertaValorAte.toString());

    // Filtros de Auditoria
    if (filters.isDeleted !== undefined) queryParams.append('isDeleted', filters.isDeleted.toString());
    if (filters.updatedBy) queryParams.append('updatedBy', filters.updatedBy);
    if (filters.deletedBy) queryParams.append('deletedBy', filters.deletedBy);

    // Busca geral
    if (filters.searchTerm) queryParams.append('searchTerm', filters.searchTerm);

    const response = await withRequestTimeout(
      (signal) =>
        this.api.get<ContractListPagedResponseDto>(`/contracts/list?${queryParams.toString()}`, {
          signal,
        }),
      options?.timeoutMs ?? 45000,
      'A listagem de contratos demorou demais. Tente novamente ou refine os filtros.',
      options?.signal
    );
    return response.data;
  }

  /**
   * Busca contrato por ID
   */
  async getContractById(id: string): Promise<ContractResponseDto> {
    const response = await this.api.get<ApiResponse<ContractResponseDto>>(`/contracts/${id}`);
    return this.extractData(response);
  }

  /** HTML auto-contido CQV (download). Requer JWT; ficheiro resultante abre offline. */
  async getCqvHtml(id: string): Promise<Blob> {
    const response = await this.api.get(`/contracts/${id}/cqv-html`, { responseType: 'blob' });
    return response.data as Blob;
  }

  async getContractForLineDetailsModal(
    id: string,
    options?: { signal?: AbortSignal }
  ): Promise<ContractLineDetailsModalDto> {
    const response = await withRequestTimeout(
      (signal) =>
        this.api.get<ApiResponse<ContractLineDetailsModalDto>>(`/contracts/${id}/line-details-modal`, {
          signal,
        }),
      30000,
      'O carregamento dos detalhes das linhas demorou demais. Tente novamente.',
      options?.signal
    );
    return this.extractData(response);
  }

  async getContractListItem(contractId: string): Promise<ContractListDto> {
    const response = await this.api.get<ContractListDto>(`/contracts/${contractId}/list-item`);
    return response.data;
  }

  /**
   * Busca cabeçalho + linhas de todos os contratos do grupo para o modal de detalhes (uma única chamada).
   */
  async getGroupLineDetailsModal(
    groupId: string,
    options?: { signal?: AbortSignal }
  ): Promise<ContractLineDetailsModalDto[]> {
    const response = await withRequestTimeout(
      (signal) =>
        this.api.get<ApiResponse<ContractLineDetailsModalDto[]>>(
          `/contracts/group/${groupId}/line-details-modal`,
          { signal }
        ),
      30000,
      'O carregamento dos detalhes do grupo demorou demais. Tente novamente.',
      options?.signal
    );
    return this.extractData(response);
  }

  /**
   * Cria novo contrato
   */
  async createContract(contractData: CreateContractRequestDto): Promise<ContractResponseDto> {
    const response = await this.api.post<ApiResponse<ContractResponseDto>>('/contracts', contractData);
    return this.extractData(response);
  }

  async createContractDraft(contractData: CreateContractRequestDto): Promise<ContractResponseDto> {
    const response = await this.api.post<ApiResponse<ContractResponseDto>>('/contracts/drafts', contractData);
    return this.extractData(response);
  }

  async updateContractDraft(id: string, contractData: CreateContractRequestDto): Promise<ContractResponseDto> {
    const response = await this.api.patch<ApiResponse<ContractResponseDto>>(`/contracts/${id}/draft`, contractData);
    return this.extractData(response);
  }

  async finalizeContractDraft(id: string, contractData: CreateContractRequestDto): Promise<ContractResponseDto> {
    const response = await this.api.post<ApiResponse<ContractResponseDto>>(`/contracts/${id}/finalize`, contractData);
    return this.extractData(response);
  }

  async getContractDraftsByClient(clientId: string): Promise<DraftSummaryDto[]> {
    const response = await this.api.get<ApiResponse<DraftSummaryDto[]>>(`/contracts/drafts?clientId=${clientId}`);
    return this.extractData(response);
  }

  async deleteContractDraft(id: string): Promise<void> {
    await this.api.delete(`/contracts/${id}/draft`);
  }

  async updateResponsibleConsultant(contractId: string, userId: string): Promise<ContractResponseDto> {
    const response = await this.api.patch<ApiResponse<ContractResponseDto>>(
      `/contracts/${contractId}/responsible-consultant`,
      { userId },
    );
    return this.extractData(response);
  }

  /**
   * Atualiza contrato existente
   */
  async updateContract(id: string, contractData: UpdateContractRequestDto): Promise<PatchContractResultDto> {
    const response = await this.api.put<ApiResponse<PatchContractResultDto>>(`/contracts/${id}`, contractData);
    return this.extractData(response);
  }

  async patchContract(id: string, data: PatchContractRequestDto): Promise<PatchContractResultDto> {
    const response = await this.api.patch<ApiResponse<PatchContractResultDto>>(`/contracts/${id}`, data);
    return this.extractData(response);
  }

  async ensureGroupForLineEdit(
    anchorContractId: string,
    data: EnsureGroupForLineEditRequestDto
  ): Promise<EnsureGroupForLineEditResponseDto> {
    const response = await this.api.post<ApiResponse<EnsureGroupForLineEditResponseDto>>(
      `/contracts/${anchorContractId}/ensure-group-for-line-edit`,
      data
    );
    return this.extractData(response);
  }

  async patchCqvWhatsappCopyState(
    id: string,
    data: PatchCqvWhatsappCopyStateRequestDto
  ): Promise<PatchCqvWhatsappCopyStateResponseDto> {
    const response = await this.api.patch<ApiResponse<PatchCqvWhatsappCopyStateResponseDto>>(
      `/contracts/${id}/cqv-whatsapp-copy-state`,
      data
    );
    return this.extractData(response);
  }

  /**
   * Remove contrato (soft delete)
   */
  async deleteContract(id: string): Promise<void> {
    const response = await this.api.delete(`/contracts/${id}`);
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao excluir contrato');
      }
    }
  }

  /**
   * Confirma/Assina um contrato (altera status para Assinado)
   */
  async confirmContract(id: string): Promise<{ success: boolean; message: string; data: { contractId: string; status: string } }> {
    const response = await this.api.put(`/contracts/${id}/confirm`);
    return response.data;
  }

  /**
   * Altera o status de um contrato
   */
  async updateContractStatus(
    id: string,
    status: EContractStatus,
    options?: {
      changeReason?: string;
      updatedBy?: string;
      suppressTrocaCarteiraAutomation?: boolean;
    }
  ): Promise<UpdateContractStatusResultDto> {
    try {
      const body: {
        status: EContractStatus;
        changeReason?: string;
        updatedBy?: string;
        suppressTrocaCarteiraAutomation?: boolean;
      } = { status };
      if (options?.changeReason?.trim()) {
        body.changeReason = options.changeReason.trim();
      }
      if (options?.updatedBy?.trim()) {
        body.updatedBy = options.updatedBy.trim();
      }
      if (options?.suppressTrocaCarteiraAutomation) {
        body.suppressTrocaCarteiraAutomation = true;
      }
      const response = await this.api.put<
        ApiResponse<UpdateContractStatusResultDto>
      >(`/contracts/${id}/status`, body);
      return this.extractData(response);
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        err?.message;
      throw new Error(apiMsg || 'Erro ao alterar status do contrato');
    }
  }

  /**
   * Confirma VTME de um contrato
   */
  async confirmarVtme(id: string, request: ConfirmarVtmeRequest): Promise<void> {
    const response = await this.api.post(`/contracts/${id}/confirmar-vtme`, request);
    if (response.data && !response.data.success) {
      throw new Error(response.data.message || 'Erro ao confirmar VTME');
    }
  }

  /**
   * Atualiza IDs de pedido VTME após confirmação. Somente Admin (validado no backend).
   */
  async updatePedidoIds(id: string, request: UpdatePedidoIdsRequest): Promise<void> {
    try {
      const response = await this.api.put(`/contracts/${id}/pedido-ids`, request);
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Erro ao atualizar IDs de pedido');
      }
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        err?.message;
      throw new Error(apiMsg || 'Erro ao atualizar IDs de pedido');
    }
  }

  /**
   * Atualiza as credenciais do Meu TIM para todas as linhas marcadas para suspensão de um contrato.
   * Somente Admin deve ter acesso a esse endpoint (validado no backend).
   */
  async updateTimCredentials(contractId: string, payload: UpdateTimCredentialsRequestDto): Promise<void> {
    await this.api.put(`/contracts/${contractId}/tim-credentials`, payload);
  }

  // Endpoints específicos para navegação

  /**
   * Busca administrador do contrato
   */
  async getContractAdministrator(contractId: string): Promise<ContractAdministratorDto> {
    const response = await this.api.get<ApiResponse<ContractAdministratorDto>>(`/contracts/${contractId}/administrator`);
    return this.extractData(response);
  }

  /**
   * Busca endereço de entrega do contrato
   */
  async getContractDeliveryAddress(contractId: string): Promise<ContractDeliveryAddressDto> {
    const response = await this.api.get<ApiResponse<ContractDeliveryAddressDto>>(`/contracts/${contractId}/delivery-address`);
    return this.extractData(response);
  }

  /**
   * Busca representantes do contrato
   */
  async getContractRepresentatives(contractId: string): Promise<ContractRepresentativeDto[]> {
    const response = await this.api.get<ApiResponse<ContractRepresentativeDto[]>>(`/contracts/${contractId}/representatives`);
    return this.extractData(response);
  }

  /**
   * Busca recebedores autorizados do contrato
   */
  async getContractAuthorizedReceivers(contractId: string): Promise<ContractAuthorizedReceiverDto[]> {
    const response = await this.api.get<ApiResponse<ContractAuthorizedReceiverDto[]>>(`/contracts/${contractId}/authorized-receivers`);
    return this.extractData(response);
  }

  /**
   * Busca testemunhas do contrato
   */
  async getContractWitnesses(contractId: string): Promise<ContractWitnessDto[]> {
    const response = await this.api.get<ApiResponse<ContractWitnessDto[]>>(`/contracts/${contractId}/witnesses`);
    return this.extractData(response);
  }

  /**
   * Busca linhas do contrato
   */
  async getContractLines(contractId: string): Promise<ContractLineDto[]> {
    const response = await this.api.get<ApiResponse<ContractLineDto[]>>(`/contracts/${contractId}/lines`);
    return this.extractData(response);
  }

  /**
   * Reenvia o e-mail com QR Code e-SIM para o administrador do cliente
   */
  async resendEsimQrCode(contractId: string): Promise<void> {
    await this.api.post(`/contracts/${contractId}/resend-esim-qrcode`);
  }

  /**
   * Atualiza o ICCID de uma linha específica do contrato
   */
  async updateLineIsApto(contractId: string, lineId: string, isApto: boolean): Promise<void> {
    try {
      const response = await this.api.put(`/contracts/${contractId}/lines/${lineId}/is-apto`, {
        isApto,
      });
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Erro ao atualizar aptidão da linha');
      }
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        err?.message;
      throw new Error(apiMsg || 'Erro ao atualizar aptidão da linha');
    }
  }

  async updateLineIccid(
    contractId: string,
    lineId: string,
    newIccid: string | null,
    motivoTroca?: string,
    fastUsado?: boolean
  ): Promise<void> {
    try {
      const response = await this.api.put(`/contracts/${contractId}/lines/${lineId}/iccid`, { 
        newIccid,
        motivoTroca,
        fastUsado: fastUsado ?? false
      });
      if (response.data && typeof response.data === 'object' && 'success' in response.data && !response.data.success) {
        throw new Error(response.data.message || 'Erro ao atualizar ICCID');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response?.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Erro ao atualizar ICCID');
    }
  }

  /**
   * Atualiza ICCIDs de múltiplas linhas do contrato em lote
   */
  async batchUpdateLineIccid(
    contractId: string,
    linhas: Array<{ lineId: string; newIccid: string | null; motivoTroca?: string; fastUsado?: boolean }>,
    options?: {
      allowAdministrativeContractStatusBypass?: boolean;
      movementSource?: FastchipIccidBatchMovementSourceForBypass;
    }
  ): Promise<void> {
    try {
      const body: {
        linhas: typeof linhas;
        allowAdministrativeContractStatusBypass?: boolean;
        movementSource?: FastchipIccidBatchMovementSourceForBypass;
      } = { linhas };
      if (options?.allowAdministrativeContractStatusBypass === true) {
        body.allowAdministrativeContractStatusBypass = true;
        if (options.movementSource) {
          body.movementSource = options.movementSource;
        }
      }
      const response = await this.api.put(`/contracts/${contractId}/lines/batch-iccid`, body);
      if (response.data && typeof response.data === 'object' && 'success' in response.data && !response.data.success) {
        throw new Error(response.data.message || 'Erro ao atualizar ICCIDs em lote');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response?.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Erro ao atualizar ICCIDs em lote');
    }
  }

  /**
   * Busca informações resumidas do contrato
   */
  async getContractSummary(contractId: string): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(`/contracts/${contractId}/summary`);
    return this.extractData(response);
  }

  /**
   * Verifica se contrato existe
   */
  async contractExists(id: string): Promise<boolean> {
    try {
      await this.getContractById(id);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Método auxiliar para criar filtros vazios
   */
  createEmptyFilters(): ContractFiltersDto {
    return {
      pageNumber: 1,
      pageSize: 10
    };
  }

  /**
   * Cria filtros para busca por status
   */
  createFiltersByStatus(status: EContractStatus, pageNumber: number = 1, pageSize: number = 10): ContractFiltersDto {
    return {
      status,
      pageNumber,
      pageSize
    };
  }

  /**
   * Cria filtros para busca por cliente
   */
  createFiltersByClient(clientId: string, pageNumber: number = 1, pageSize: number = 10): ContractFiltersDto {
    return {
      clientId,
      pageNumber,
      pageSize
    };
  }

  /**
   * Cria filtros para busca por período
   */
  createFiltersByPeriod(
    dataInicio: Date, 
    dataFim: Date, 
    pageNumber: number = 1, 
    pageSize: number = 10
  ): ContractFiltersDto {
    return {
      dataAssinaturaDe: dataInicio,
      dataAssinaturaAte: dataFim,
      pageNumber,
      pageSize
    };
  }

  /**
   * Cria filtros para busca geral
   */
  createFiltersBySearchTerm(searchTerm: string, pageNumber: number = 1, pageSize: number = 10): ContractFiltersDto {
    return {
      searchTerm,
      pageNumber,
      pageSize
    };
  }

  /**
   * Baixar PDF do contrato
   */
  async downloadContractPdf(contractId: string): Promise<void> {
    try {
      const response = await this.api.get(`/contracts/${contractId}/pdf`, {
        responseType: 'blob'
      });

      // Criar URL do blob para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar PDF do contrato:', error);
      throw new Error('Erro ao baixar PDF do contrato');
    }
  }

  async downloadTimAditivoContractPdf(contractId: string): Promise<void> {
    try {
      const response = await this.api.get(`/contracts/${contractId}/tim-aditivo-pdf`, {
        responseType: 'blob'
      });

      // Criar URL do blob para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_tim_aditivo_${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar PDF TIM Aditivo do contrato:', error);
      throw new Error('Erro ao baixar PDF TIM Aditivo do contrato');
    }
  }

  /**
   * Baixar PDF TIM Aditivo do contrato
   */
  async downloadTimAditivoPdf(contractId: string): Promise<void> {
    try {
      const response = await this.api.get(`/contracts/${contractId}/tim-aditivo-pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_tim_aditivo_${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar PDF TIM Aditivo do contrato:', error);
      throw new Error('Erro ao baixar PDF TIM Aditivo do contrato');
    }
  }

  /**
   * Baixa todos os contratos (Aditivo, Proposta Comercial e Renegociação) em um arquivo ZIP
   */
  async downloadAllContractsZip(contractId: string): Promise<void> {
    try {
      const response = await this.api.get(`/contracts/${contractId}/all-pdfs`, {
        responseType: 'blob'
      });

      // Extrair nome do arquivo do header Content-Disposition
      let fileName = 'contratos.zip';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar ZIP dos contratos:', error);
      throw new Error('Erro ao baixar ZIP dos contratos');
    }
  }

  /**
   * Baixa o PDF do Termo de Compromisso
   */
  async downloadTimTermoCompromissoPdf(contractId: string): Promise<void> {
    try {
      const response = await this.api.get(`/contracts/${contractId}/tim-termo-compromisso-pdf`, {
        responseType: 'blob'
      });

      // Extrair nome do arquivo do header Content-Disposition
      let fileName = `termo_compromisso_${contractId}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar PDF do Termo de Compromisso:', error);
      throw new Error('Erro ao baixar PDF do Termo de Compromisso');
    }
  }

  /**
   * Método auxiliar para verificar se filtros têm valores
   */
  hasFilters(filters: ContractFiltersDto): boolean {
    return !!(
      // Filtros por Cliente
      filters.clientId ||
      filters.clientCnpj ||
      filters.clientNomeFantasia ||
      filters.clientRazaoSocial ||
      filters.clientEstado ||
      filters.clientCidade ||
      filters.clientSearch ||
      
      // Filtros por Usuário
      filters.userId ||
      filters.userName ||
      
      // Filtros por Contrato (status 0 = Criado é válido; não usar truthiness)
      filters.status !== undefined ||
      filters.tipoContratacao ||
      filters.token ||
      (filters.effectiveClusters && filters.effectiveClusters.length > 0) ||
      filters.fastchip !== undefined ||
      filters.numeroP2B ||
      filters.contractId ||
      filters.iccid ||
      filters.temTrocaLinha !== undefined ||
      filters.temPendenciaSuspensaoLinhas !== undefined ||
      filters.temAparelhoCatalogo !== undefined ||
      
      // Filtros por Data
      filters.dataAssinaturaDe ||
      filters.dataAssinaturaAte ||
      filters.dataVendaDe ||
      filters.dataVendaAte ||
      filters.createdAtDe ||
      filters.createdAtAte ||
      
      // Filtros por Valores
      filters.valorTotalMensalDe ||
      filters.valorTotalMensalAte ||
      filters.comprometimentoMinimo ||
      
      // Filtros por Endereço
      filters.enderecoCep ||
      filters.enderecoCidade ||
      filters.enderecoEstado ||
      
      // Filtros por Administrador
      filters.administratorNome ||
      filters.administratorCpf ||
      filters.administratorEmail ||
      
      // Filtros por Representante
      filters.representativeNome ||
      filters.representativeCpf ||
      
      // Filtros por Testemunha
      filters.witnessNome ||
      filters.witnessCpf ||
      
      // Filtros por Linha
      filters.linhaNegociacao ||
      filters.linhaDdd ||
      filters.linhaTipoVenda ||
      filters.planId ||
      filters.planName ||
      
      // Filtros por Oferta
      filters.ofertaNome ||
      filters.ofertaValorDe ||
      filters.ofertaValorAte ||
      
      // Filtros de Auditoria
      filters.isDeleted !== undefined ||
      filters.updatedBy ||
      filters.deletedBy ||
      
      // Busca geral
      filters.searchTerm
    );
  }

  /** DocuSign desativado na API → 410 Gone; mensagem amigável para UI residual. */
  private rethrowIfDocusignGone(error: unknown): never {
    const status = (error as { response?: { status?: number; data?: { message?: string } } })?.response?.status;
    if (status === 410) {
      const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      throw new Error(apiMessage || 'Módulo DocuSign descontinuado.');
    }
    throw error;
  }

  /**
   * Envia contrato para processamento no DocuSign via RPA
   */
  async sendContractToDocusign(contractId: string, payload?: SendContractToDocusignRequest): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.api.post<ApiResponse<{ success: boolean; message: string }>>(
        `/contracts/${contractId}/send-to-docusign`,
        payload
      );
      return this.extractData(response);
    } catch (error) {
      this.rethrowIfDocusignGone(error);
    }
  }

  async sendGroupToDocusign(
    groupId: string,
    payload?: SendContractToDocusignRequest
  ): Promise<SendGroupToDocusignResponse> {
    try {
      const response = await this.api.post<ApiResponse<SendGroupToDocusignResponse>>(
        `/contracts/group/${groupId}/send-to-docusign`,
        payload
      );
      return this.extractData(response);
    } catch (error) {
      this.rethrowIfDocusignGone(error);
    }
  }

  async retryGroupDocusignProcessing(
    groupId: string,
    payload?: SendContractToDocusignRequest
  ): Promise<{ success: boolean; message: string; groupId?: string; leaderContractId?: string; processId?: string }> {
    try {
      const response = await this.api.post<
        ApiResponse<{ success: boolean; message: string; groupId?: string; leaderContractId?: string; processId?: string }>
      >(`/contracts/group/${groupId}/retry-docusign-processing`, payload);
      return this.extractData(response);
    } catch (error) {
      this.rethrowIfDocusignGone(error);
    }
  }

  async cancelDocusignProcessing(
    contractId: string,
    contractIds?: string[]
  ): Promise<{ success: boolean; message: string; contractId?: string }> {
    try {
      const payload: CancelDocusignProcessingRequest = { contractIds };
      const response = await this.api.post<ApiResponse<{ success: boolean; message: string; contractId?: string }>>(
        `/contracts/${contractId}/cancel-docusign-processing`,
        payload
      );
      return this.extractData(response);
    } catch (error) {
      this.rethrowIfDocusignGone(error);
    }
  }

  async retryDocusignProcessing(contractId: string): Promise<{ success: boolean; message: string; contractId?: string; processId?: string }> {
    try {
      const response = await this.api.post<ApiResponse<{ success: boolean; message: string; contractId?: string; processId?: string }>>(
        `/contracts/${contractId}/retry-docusign-processing`
      );
      return this.extractData(response);
    } catch (error) {
      this.rethrowIfDocusignGone(error);
    }
  }

  async getDocusignSendHistory(contractId: string): Promise<ContractDocusignSendHistoryDto[]> {
    const response = await this.api.get<ApiResponse<ContractDocusignSendHistoryDto[]>>(
      `/contracts/${contractId}/docusign-send-history`
    );
    return this.extractData(response);
  }

  async getDocusignSendProgress(contractId: string): Promise<DocusignSendProgressResponseDto> {
    const response = await this.api.get<ApiResponse<DocusignSendProgressResponseDto>>(
      `/contracts/${contractId}/docusign-send-progress`
    );
    return this.extractData(response);
  }

  /**
   * Verifica se existe contrato duplicado para o cliente
   */
  async checkForDuplicate(clientId: string, tipoContratacao?: string, hoursWindow: number = 24): Promise<{ hasDuplicate: boolean; contract: ContractResponseDto | null }> {
    const queryParams = new URLSearchParams();
    if (tipoContratacao) queryParams.append('tipoContratacao', tipoContratacao);
    queryParams.append('hoursWindow', hoursWindow.toString());
    
    const response = await this.api.get<{ hasDuplicate: boolean; contract: ContractResponseDto | null }>(`/contracts/check-duplicate/${clientId}?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * GET /contracts/suggested-sale-token — token de cluster (Senior) pela mesma regra do create/update.
   */
  async getSuggestedSaleToken(
    clientId: string,
    options?: { deliveryCidade?: string; deliveryEstado?: string }
  ): Promise<string> {
    return requestSuggestedSaleToken(this.api, clientId, options);
  }

  async hasExistingContractByCnpj(cnpj: string): Promise<boolean> {
    const queryParams = new URLSearchParams();
    queryParams.append('cnpj', cnpj);
    
    const response = await this.api.get<{ hasExisting: boolean }>(`/contracts/has-existing-by-cnpj?${queryParams.toString()}`);
    return response.data.hasExisting;
  }

  async cancelPreviousContractByCnpj(cnpj: string): Promise<void> {
    const queryParams = new URLSearchParams();
    queryParams.append('cnpj', cnpj);
    
    await this.api.post(`/contracts/cancel-previous-by-cnpj?${queryParams.toString()}`);
  }

  /**
   * Busca contratos de um mesmo grupo
   */
  async getContractsByGroupId(groupId: string): Promise<ContractResponseDto[]> {
    const response = await this.api.get<ApiResponse<ContractResponseDto[]>>(`/contracts/group/${groupId}`);
    return this.extractData(response);
  }

  /**
   * Baixa PDFs de todos os contratos de um grupo em ZIP
   */
  async downloadGroupPdfs(groupId: string): Promise<void> {
    try {
      const response = await this.api.get(`/contracts/group/${groupId}/all-pdfs`, {
        responseType: 'blob'
      });

      let fileName = `contratos_grupo_${groupId}.zip`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar ZIP dos contratos do grupo:', error);
      throw new Error('Erro ao baixar ZIP dos contratos do grupo');
    }
  }

  async getEmiliContracts(filters: {
    startDate?: string;
    endDate?: string;
    status?: EContractStatus;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ContractPagedResponseDto> {
    const params: any = {};
    
    if (filters.startDate) {
      params.startDate = filters.startDate;
    }
    if (filters.endDate) {
      params.endDate = filters.endDate;
    }
    if (filters.status !== undefined) {
      params.status = filters.status;
    }
    if (filters.pageNumber !== undefined) {
      params.pageNumber = filters.pageNumber;
    }
    if (filters.pageSize !== undefined) {
      params.pageSize = filters.pageSize;
    }

    const response = await this.api.get<ApiResponse<ContractPagedResponseDto>>('/contracts/emili-contracts', { params });
    return this.extractData(response);
  }

  /**
   * Busca contratos pendentes do Seller (status "Criado")
   */
  async getPendingSellerContracts(): Promise<PendingSellerContractDto[]> {
    const response = await this.api.get<PendingSellerContractDto[]>('/contracts/pending-seller-updates');
    return response.data;
  }

  /**
   * Contador leve de contratos pendentes do Seller (mesmo filtro de pending-seller-updates).
   * Usado no poll do Header; a lista completa fica para open do sino / pré-check de create.
   */
  async getPendingSellerCount(): Promise<number> {
    const response = await this.api.get<{ count: number }>('/contracts/pending-seller-count');
    return response.data?.count ?? 0;
  }

  /**
   * Busca contratos pendentes da equipe do TeamLeader (status "Criado")
   */
  async getPendingTeamContracts(): Promise<PendingSellerContractDto[]> {
    const response = await this.api.get<PendingSellerContractDto[]>('/contracts/pending-team-updates');
    return response.data;
  }

  /**
   * Contador leve de contratos pendentes da equipe (TeamLeader).
   */
  async getPendingTeamCount(): Promise<number> {
    const response = await this.api.get<{ count: number }>('/contracts/pending-team-count');
    return response.data?.count ?? 0;
  }

  /**
   * Atualiza o código de rastreio de um contrato
   */
  async updateTrackingCode(contractId: string, trackingCode: string): Promise<void> {
    const response = await this.api.put(`/contracts/${contractId}/tracking-code`, { trackingCode });
    if (response.data && typeof response.data === 'object' && 'success' in response.data && !response.data.success) {
      throw new Error(response.data.message || 'Erro ao atualizar código de rastreio');
    }
  }

  /**
   * Marca um contrato como enviado, atualizando DataEnvio, TrackingCode e Status
   */
  async markContractAsSent(contractId: string, trackingCode?: string): Promise<void> {
    const response = await this.api.put(`/contracts/${contractId}/mark-as-sent`, { trackingCode });
    if (response.data && typeof response.data === 'object' && 'success' in response.data && !response.data.success) {
      throw new Error(response.data.message || 'Erro ao marcar contrato como enviado');
    }
  }

  async markFastchipEnviado(contractId: string): Promise<{ fastchipEnviadoEm: string }> {
    const response = await this.api.post(`/contracts/${contractId}/fastchip-enviado`);
    if (response.data && typeof response.data === 'object' && 'success' in response.data && !response.data.success) {
      throw new Error(response.data.message || 'Erro ao marcar fastchip como enviado');
    }
    return response.data;
  }

  /**
   * Busca linhas marcadas como "Troca de Linha" que requerem suspensão manual
   */
  async getTrocaLinhaLines(): Promise<any[]> {
    const response = await this.api.get('/v1/notifications/troca-linha');
    return response.data;
  }

  /**
   * Busca contador de linhas "Troca de Linha" pendentes
   */
  async getTrocaLinhaCount(): Promise<number> {
    const response = await this.api.get('/v1/notifications/troca-linha-count');
    return response.data.count;
  }

  /**
   * Busca timeline de rastreamento pelo contrato (API usa TrackingCode persistido no contrato).
   */
  async getContractTracking(contractId: string): Promise<TrackingInfoDto> {
    const response = await this.api.get<{ success?: boolean; data?: TrackingInfoDto; message?: string }>(
      `/contracts/${contractId}/rastreamento`
    );
    const body = response.data as { success?: boolean; data?: TrackingInfoDto; message?: string };
    if (body && typeof body === 'object' && body.success === false) {
      throw new Error(body.message || 'Erro ao consultar rastreamento');
    }
    if (body?.data) {
      return body.data;
    }
    throw new Error('Resposta de rastreamento inválida');
  }

  /**
   * Notifica edição de contrato criado há mais de 24h
   */
  async notifyContractEdit(contractId: string): Promise<void> {
    await this.api.post(`/contracts/${contractId}/notify-edit`);
  }

  /**
   * Envia email com dados do contrato para o destinatário especificado
   */
  async sendContractEmail(contractId: string, toEmail: string): Promise<void> {
    const response = await this.api.post(`/contracts/${contractId}/send-email`, { toEmail });
    if (response.data && !response.data.success) {
      throw new Error(response.data.message || 'Erro ao enviar email');
    }
  }

  /**
   * Envia email de recusa de contrato para o líder da equipe
   */
  async sendContractRejectionEmail(
    contractId: string,
    rejectionReason: string,
    creatorEmail?: string,
    skipCentralNotification?: boolean,
  ): Promise<{ success: boolean; emailSent: boolean; centralNotified?: boolean; reason?: string }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await this.api.post(
        `/contracts/${contractId}/send-rejection-email`,
        {
          rejectionReason,
          creatorEmail,
          skipCentralNotification: skipCentralNotification === true,
        },
        { signal: controller.signal }
      );
      
      clearTimeout(timeoutId);
      return response.data || { success: true, emailSent: true };
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Timeout ao enviar email de recusa');
      }
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  /**
   * Envia email de recusa na Qualidade (Central + SMTP, mesmo fluxo do CQV).
   */
  async sendContractQualidadeRejectionEmail(
    contractId: string,
    rejectionReason: string,
    creatorEmail?: string
  ): Promise<{ success: boolean; emailSent: boolean; reason?: string }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await this.api.post(
        `/contracts/${contractId}/send-qualidade-rejection-email`,
        { rejectionReason, creatorEmail },
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);
      return response.data || { success: true, emailSent: true };
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Timeout ao enviar email de recusa');
      }
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  /**
   * Envia email de recusa na etapa CVP (Central + SMTP, mesmo fluxo do CQV).
   */
  async sendContractCvpRejectionEmail(
    contractId: string,
    rejectionReason: string,
    creatorEmail?: string
  ): Promise<{ success: boolean; emailSent: boolean; reason?: string }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await this.api.post(
        `/contracts/${contractId}/send-cvp-rejection-email`,
        { rejectionReason, creatorEmail },
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);
      return response.data || { success: true, emailSent: true };
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Timeout ao enviar email de recusa');
      }
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  /**
   * Busca o histórico completo de mudanças de status de um contrato
   */
  async getContractStatusHistory(contractId: string): Promise<ContractStatusHistoryDto[]> {
    const response = await this.api.get<ApiResponse<ContractStatusHistoryDto[]>>(
      `/contracts/${contractId}/status-history`
    );
    return this.extractData(response);
  }

  async getCarteiraAutomacaoUltimoResultado(
    contractId: string,
    groupId?: string | null,
    options?: { fresh?: boolean; signal?: AbortSignal }
  ): Promise<CarteiraAutomacaoUltimoResultadoDto | null> {
    try {
      const params: { groupId?: string; fresh?: boolean } = {};
      if (groupId && groupId.trim() !== '') {
        params.groupId = groupId.trim();
      }
      if (options?.fresh) {
        params.fresh = true;
      }
      const response = await this.api.get<ApiResponse<CarteiraAutomacaoUltimoResultadoDto>>(
        `/contracts/${contractId}/carteira-automacao/ultimo-resultado`,
        {
          params: Object.keys(params).length > 0 ? params : undefined,
          signal: options?.signal,
        }
      );
      return this.extractData(response);
    } catch {
      return null;
    }
  }

  async getTrocaCarteiraSolicitacao(
    contractId: string,
    cnpj: string
  ): Promise<TrocaCarteiraSolicitacaoDto | null> {
    try {
      const cnpjDigits = cnpj.replace(/\D/g, '');
      const response = await this.api.get<ApiResponse<TrocaCarteiraSolicitacaoDto>>(
        `/contracts/${contractId}/troca-carteira-solicitacao`,
        { params: { cnpj: cnpjDigits } }
      );
      return this.extractData(response);
    } catch {
      return null;
    }
  }

  private async postCqvEmTrocaSolicitada(
    contractId: string,
    path: 'aprovar' | 'recusar' | 'sincronizar-atc',
    body?: { rejectionReason: string }
  ): Promise<CqvEmTrocaSolicitadaResultDto | null> {
    try {
      const response = await this.api.post<ApiResponse<CqvEmTrocaSolicitadaResultDto>>(
        `/contracts/${contractId}/cqv-em-troca-solicitada/${path}`,
        body
      );
      const data = this.extractData(response);
      if (data) return data;
      return { success: true, ...(response.data as { message?: string }) };
    } catch (err: unknown) {
      const response = (err as { response?: { data?: { data?: CqvEmTrocaSolicitadaResultDto; message?: string } } })
        ?.response;
      const data = response?.data?.data;
      if (data) return data;
      const apiMessage =
        (typeof response?.data?.message === 'string' && response.data.message) ||
        (err instanceof Error ? err.message : '') ||
        '';
      if (apiMessage) {
        return { success: false, message: apiMessage, errorCode: 'api_error' };
      }
      return null;
    }
  }

  async aprovarCqvEmTrocaSolicitada(contractId: string): Promise<CqvEmTrocaSolicitadaResultDto | null> {
    return this.postCqvEmTrocaSolicitada(contractId, 'aprovar');
  }

  async recusarCqvEmTrocaSolicitada(
    contractId: string,
    rejectionReason: string
  ): Promise<CqvEmTrocaSolicitadaResultDto | null> {
    return this.postCqvEmTrocaSolicitada(contractId, 'recusar', { rejectionReason });
  }

  async sincronizarAtcCqvEmTrocaSolicitada(
    contractId: string
  ): Promise<CqvEmTrocaSolicitadaResultDto | null> {
    return this.postCqvEmTrocaSolicitada(contractId, 'sincronizar-atc');
  }

  async reconsultarCarteiraVtme(
    contractId: string
  ): Promise<ReconsultarCarteiraVtmeResultDto | null> {
    try {
      const response = await this.api.post<ApiResponse<ReconsultarCarteiraVtmeResultDto>>(
        `/contracts/${contractId}/reconsultar-carteira-vtme`
      );
      return this.extractData(response);
    } catch (err: unknown) {
      const response = (err as { response?: { data?: { data?: ReconsultarCarteiraVtmeResultDto; message?: string } } })
        ?.response;
      const data = response?.data?.data;
      if (data) {
        return data;
      }
      const apiMessage =
        (typeof response?.data?.message === 'string' && response.data.message) ||
        (err instanceof Error ? err.message : '') ||
        '';
      if (apiMessage) {
        return {
          success: false,
          mensagemUsuario: apiMessage,
          error: 'api_error',
        };
      }
      return null;
    }
  }

  async startVerificarTrocaCarteira(contractId: string): Promise<{
    jobId: string | null;
    /** API já gravou status 63 no grupo (mesmo se a ATC falhar ao enfileirar). */
    verificandoCarteiraApplied: boolean;
    /** Conflito de negócio (ex.: já em 42/62) — não usar fallback para Troca de Carteira. */
    errorMessage?: string;
  }> {
    try {
      const response = await this.api.post<
        ApiResponse<{
          jobId?: string;
          verificandoCarteira?: boolean;
          carteiraVerificacao?: CarteiraVerificacaoResponseDto;
        }>
      >(`/contracts/${contractId}/verificar-troca-carteira`);
      const data = this.extractData(response);
      const cv = data?.carteiraVerificacao;
      const iniciada = Boolean(cv?.iniciada);
      const verificandoAplicado = Boolean(data?.verificandoCarteira ?? iniciada);
      return {
        jobId: cv?.jobId ?? data?.jobId ?? null,
        verificandoCarteiraApplied: verificandoAplicado,
        errorMessage:
          iniciada === false && typeof cv?.erro === 'string' ? cv.erro : undefined,
      };
    } catch (err: unknown) {
      const response = (err as {
        response?: {
          status?: number;
          data?: {
            message?: string;
            data?: {
              carteiraVerificacao?: CarteiraVerificacaoResponseDto;
            };
          };
        };
      })?.response;
      const status = response?.status;
      const apiMessage =
        (typeof response?.data?.message === 'string' && response.data.message) ||
        (err instanceof Error ? err.message : '') ||
        '';
      const cv = response?.data?.data?.carteiraVerificacao;
      const erroCv =
        (typeof cv?.erro === 'string' && cv.erro) || apiMessage || undefined;

      if (status === 503) {
        return {
          jobId: null,
          verificandoCarteiraApplied: false,
          errorMessage:
            erroCv ||
            'Não foi possível iniciar a verificação de carteira no VTME.',
        };
      }

      if (status === 409) {
        const jaNoFluxoTroca =
          apiMessage.toLowerCase().includes('fluxo de troca de carteira') ||
          apiMessage.toLowerCase().includes('troca de carteira.');
        if (jaNoFluxoTroca) {
          return {
            jobId: null,
            verificandoCarteiraApplied: false,
            errorMessage:
              apiMessage || 'Contrato já está no fluxo de troca de carteira.',
          };
        }
        return { jobId: null, verificandoCarteiraApplied: true };
      }

      return {
        jobId: null,
        verificandoCarteiraApplied: false,
        errorMessage: apiMessage || undefined,
      };
    }
  }

  async getContractAuditLog(contractId: string): Promise<ContractAuditLogDto[]> {
    const response = await this.api.get<ApiResponse<ContractAuditLogDto[]>>(
      `/contracts/${contractId}/audit-log`
    );
    return this.extractData(response);
  }

  async getContractLineAuditLog(contractId: string): Promise<ContractLineAuditLogDto[]> {
    const response = await this.api.get<ApiResponse<ContractLineAuditLogDto[]>>(
      `/contracts/${contractId}/line-audit-log`
    );
    return this.extractData(response);
  }

  async getGroupLineAuditLog(groupId: string): Promise<GroupContractLineAuditLogResponseDto> {
    const response = await this.api.get<ApiResponse<GroupContractLineAuditLogResponseDto>>(
      `/contracts/group/${groupId}/line-audit-log`
    );
    return this.extractData(response);
  }

  async getInactiveJobSettings(): Promise<ContractInactiveJobSettingsDto> {
    const response = await this.api.get<ApiResponse<ContractInactiveJobSettingsDto>>(
      '/contracts/operations/inactive-settings'
    );
    return this.extractData(response);
  }

  async updateInactiveJobSettings(
    settings: ContractInactiveJobSettingsDto
  ): Promise<ContractInactiveJobSettingsDto> {
    const response = await this.api.put<ApiResponse<ContractInactiveJobSettingsDto>>(
      '/contracts/operations/inactive-settings',
      settings
    );
    return this.extractData(response);
  }

  async getInactivePreview(limit = 100): Promise<ContractInactivePreviewResponseDto> {
    const response = await this.api.get<ApiResponse<ContractInactivePreviewResponseDto>>(
      '/contracts/operations/inactive-preview',
      { params: { limit } }
    );
    return this.extractData(response);
  }

  async downloadInactivePreviewExport(limit = 1000): Promise<void> {
    await this.downloadBlob(
      '/contracts/operations/inactive-preview/export',
      `fila-inativos-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`,
      { params: { limit } }
    );
  }

  async getInactiveCancellationRuns(
    limit = 50
  ): Promise<ContractInactiveCancellationRunListItemDto[]> {
    const response = await this.api.get<
      ApiResponse<ContractInactiveCancellationRunListItemDto[]>
    >('/contracts/operations/inactive-runs', { params: { limit } });
    return this.extractData(response);
  }

  async downloadInactiveRunReport(runId: string): Promise<void> {
    await this.downloadBlob(
      `/contracts/operations/inactive-runs/${runId}/report`,
      `laudo-inativos-${runId}.xlsx`
    );
  }

  async getRestorationHistory(limit = 200): Promise<ContractRestorationHistoryItemDto[]> {
    const response = await this.api.get<ApiResponse<ContractRestorationHistoryItemDto[]>>(
      '/contracts/operations/restoration-history',
      { params: { limit } }
    );
    return this.extractData(response);
  }

  async getClientContractHistory(params: {
    cnpj?: string;
    razaoSocial?: string;
    cpf?: string;
    telefone?: string;
    status?: EContractStatus;
    limit?: number;
  }): Promise<ClientContractHistoryResponseDto> {
    const response = await this.api.get<ApiResponse<ClientContractHistoryResponseDto>>(
      '/contracts/operations/client-history',
      { params }
    );
    return this.extractData(response);
  }

  async getClientContractHub(params: {
    clientId?: string;
    cnpj?: string;
    razaoSocial?: string;
    cpf?: string;
    telefone?: string;
    status?: EContractStatus;
    timelineLimit?: number;
  }): Promise<ClientContractHubResponseDto> {
    const response = await this.api.get<ApiResponse<ClientContractHubResponseDto>>(
      '/contracts/clients/hub',
      { params }
    );
    return this.extractData(response);
  }

  async exportClientContractHistory(request: ClientContractHistoryExportRequestDto): Promise<void> {
    const response = await this.api.post('/contracts/operations/client-history/export', request, {
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const fileName =
      disposition?.match(/filename="?([^"]+)"?/)?.[1] ??
      `historico-cliente-${new Date().toISOString().slice(0, 10)}.xlsx`;
    this.triggerBlobDownload(response.data, fileName);
  }

  async restoreCancelledContracts(
    request: RestoreCancelledContractsRequestDto
  ): Promise<RestoreCancelledContractsResultDto> {
    const response = await this.api.post<ApiResponse<RestoreCancelledContractsResultDto>>(
      '/contracts/restore-cancelled',
      request
    );
    return this.extractData(response);
  }

  async exportRestoreCancelledReport(result: RestoreCancelledContractsResultDto): Promise<void> {
    const response = await this.api.post('/contracts/restore-cancelled/export', result, {
      responseType: 'blob',
    });
    const fileName = `restore-contratos-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;
    this.triggerBlobDownload(response.data, fileName);
  }

  /** Estado atual do ciclo de correção manual (SLA 2) de um contrato — sem cache, reflete o último `start`/`end`. */
  async getCorrectionCycle(contractId: string): Promise<ContractCorrectionCycleStateDto> {
    const response = await this.api.get<ApiResponse<ContractCorrectionCycleStateDto>>(
      `/contracts/${contractId}/correction-cycle`
    );
    return this.extractData(response);
  }

  /** Abre o ciclo de correção manual (SLA 2). 400 = status fora do gate, 409 = já existe ciclo aberto. */
  async startCorrectionCycle(
    contractId: string,
    body: ContractCorrectionActionRequest
  ): Promise<ContractCorrectionCycleStateDto> {
    try {
      const response = await this.api.post<ApiResponse<ContractCorrectionCycleStateDto>>(
        `/contracts/${contractId}/correction-cycle/start`,
        body
      );
      return this.extractData(response);
    } catch (err: unknown) {
      throw new Error(this.extractCorrectionCycleErrorMessage(err, 'Não foi possível iniciar a correção.'));
    }
  }

  /** Fecha o ciclo de correção manual (SLA 2). 403 = ciclo de outro usuário, 409 = não há ciclo aberto. */
  async endCorrectionCycle(
    contractId: string,
    body: ContractCorrectionActionRequest
  ): Promise<ContractCorrectionCycleStateDto> {
    try {
      const response = await this.api.post<ApiResponse<ContractCorrectionCycleStateDto>>(
        `/contracts/${contractId}/correction-cycle/end`,
        body
      );
      return this.extractData(response);
    } catch (err: unknown) {
      throw new Error(this.extractCorrectionCycleErrorMessage(err, 'Não foi possível finalizar a correção.'));
    }
  }

  private extractCorrectionCycleErrorMessage(err: unknown, fallback: string): string {
    const response = (err as { response?: { data?: { message?: string } } })?.response;
    const apiMessage =
      (typeof response?.data?.message === 'string' && response.data.message) ||
      (err instanceof Error ? err.message : '') ||
      '';
    return apiMessage || fallback;
  }

  private triggerBlobDownload(data: BlobPart, fileName: string): void {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private async downloadBlob(
    url: string,
    fileName: string,
    config?: { params?: Record<string, unknown> }
  ): Promise<void> {
    const response = await this.api.get(url, { responseType: 'blob', ...config });
    this.triggerBlobDownload(response.data, fileName);
  }
}

/** Estado do ciclo de correção manual (SLA 2) — botões "Comecei a corrigir" / "Terminei a correção" no modal de linhas. */
export interface ContractCorrectionCycleStateDto {
  hasOpenCycle: boolean;
  startedAtUtc?: string | null;
  startedByUserId?: string | null;
  startedByUserName?: string | null;
  /** Usuário logado é quem abriu o ciclo aberto (habilita "Terminei a correção"). */
  startedByCurrentUser: boolean;
}

export interface ContractCorrectionActionRequest {
  reason?: string;
}

export interface RestoreCancelledContractsRequestDto {
  contractIds: string[];
  statusOverrides?: RestoreContractStatusOverrideDto[];
  sendEmailReport?: boolean;
}

export interface RestoreContractStatusOverrideDto {
  contractId: string;
  status: EContractStatus;
}

export interface RestoreCancelledContractsResultDto {
  executedAtUtc: string;
  requestedContracts: number;
  restoredContracts: number;
  failedContracts: number;
  contracts: RestoreCancelledContractItemResultDto[];
}

export interface RestoreCancelledContractItemResultDto {
  contractId: string;
  clientRazaoSocial?: string;
  clientCnpj?: string;
  previousStatus?: EContractStatus;
  targetStatus?: EContractStatus;
  appliedStatus?: EContractStatus;
  cancelledAt?: string;
  restored: boolean;
  message?: string;
  lines: RestoreCancelledLineResultDto[];
}

export interface RestoreCancelledLineResultDto {
  lineId: string;
  previousIccid?: string;
  currentIccid?: string;
  suspensionReactivated: boolean;
  outcome: string;
  message?: string;
}

export interface ContractInactiveJobSettingsDto {
  enabled: boolean;
  cronExpression: string;
  inactiveThresholdHours: number;
  timeZone: string;
  jobRunDaysOfWeek: number[];
  excludedDates: string[];
  sendReportEmailEnabled: boolean;
  persistedInDatabase?: boolean;
  updatedAtUtc?: string;
}

export interface ContractInactiveCancellationRunListItemDto {
  id: string;
  executedAtUtc: string;
  cutoffUtc: string;
  eligibleContracts: number;
  cancelledContracts: number;
  failedContracts: number;
  liberatedIccids: number;
  skipped: boolean;
  skipReason?: string;
  emailSent: boolean;
  hasReport: boolean;
}

export interface ContractRestorationHistoryItemDto {
  id: string;
  contractId: string;
  clientRazaoSocial?: string;
  clientCnpj?: string;
  previousStatus?: EContractStatus;
  targetStatus?: EContractStatus;
  appliedStatus?: EContractStatus;
  cancelledAtUtc?: string;
  restored: boolean;
  message?: string;
  restoredByUserName?: string;
  createdAtUtc: string;
}

export interface ContractInactivePreviewResponseDto {
  generatedAtUtc: string;
  cutoffUtc: string;
  totalEligible: number;
  items: ContractInactivePreviewItemDto[];
}

export interface ContractInactivePreviewItemDto {
  contractId: string;
  clientRazaoSocial?: string;
  clientCnpj?: string;
  status?: EContractStatus;
  lastMovementAtUtc: string;
  iccidCount: number;
}

export interface ClientContractHistoryResponseDto {
  total: number;
  items: ClientContractHistoryItemDto[];
}

export interface ClientContractHistoryItemDto {
  contractId: string;
  clientRazaoSocial?: string;
  clientCnpj?: string;
  clientPhone1?: string;
  administratorCpf?: string;
  currentStatus?: EContractStatus;
  eventStatus?: EContractStatus;
  previousStatus?: EContractStatus;
  changedAtUtc: string;
  changedByUserName?: string;
  changeReason?: string;
}

export interface ClientContractHistoryExportRequestDto {
  cnpj?: string;
  razaoSocial?: string;
  cpf?: string;
  telefone?: string;
  status?: EContractStatus;
  limit?: number;
  sendEmail?: boolean;
  toEmail?: string;
}

export interface ClientContractHubResponseDto {
  client?: ClientContractHubClientDto;
  summary: ClientContractHubSummaryDto;
  contracts: ClientContractHubContractDto[];
  timeline: ClientContractHubTimelineEventDto[];
  lines: ClientContractHubLineDto[];
  observations?: ClientObservationEntryDto[];
}

export interface ClientObservationEntryDto {
  id: string;
  clientId: string;
  occurredAtUtc: string;
  collaboratorUserId?: string | null;
  collaboratorName: string;
  occurrence: string;
  observation?: string | null;
  sourceType: string;
  contractId?: string | null;
}

export interface ClientContractHubClientDto {
  id: string;
  razaoSocial: string;
  cnpj: string;
  clientClassification?: string | null;
  internalNotes?: string | null;
  createdAt: string;
  estado?: string;
  cidade?: string;
  telefone1?: string;
  telefone2?: string;
  administratorNome?: string;
  administratorCpf?: string;
  administratorEmail?: string;
}

export interface ClientContractHubSummaryDto {
  totalContracts: number;
  cancelledContracts: number;
  totalLines: number;
  linesRequerSuspensao: number;
  linesSuspensas: number;
  fastchipContracts: number;
  distinctGroups: number;
}

export interface ClientContractHubContractDto {
  id: string;
  tipoContratacao: string;
  contractType?: string;
  internalNotes?: string | null;
  status?: EContractStatus | string | number;
  groupId?: string;
  token?: string;
  fastchip: boolean;
  createdAt: string;
  updatedAt?: string;
  dataAssinatura?: string;
  lastStatusAt?: string;
  lineCount: number;
  valorTotalMensal: number;
}

export interface ClientContractHubTimelineEventDto {
  contractId: string;
  contractTipo?: string;
  eventType: string;
  occurredAtUtc: string;
  eventStatus?: EContractStatus | string | number;
  previousStatus?: EContractStatus | string | number;
  changedByUserName?: string;
  changeReason?: string;
  eventTitle?: string;
  eventDetail?: string;
  isInferred?: boolean;
  posVendaProtocoloId?: string;
  posVendaProtocoloNumero?: string;
  posVendaProtocoloTipo?: string;
  posVendaProtocoloStatus?: string;
  posVendaSlaEstado?: string;
  posVendaResponsavelNome?: string;
}

export interface ClientContractHubLineDto {
  contractId: string;
  contractTipo?: string;
  lineId: string;
  ddd?: string;
  numero?: string;
  chipCcid?: string;
  requerSuspensao: boolean;
  estaSuspensa: boolean;
  dataProximaSuspensao?: string;
  planName?: string;
}

// Instância singleton
export const contractService = new ContractService();

/**
 * Mesmo GET que {@link ContractService.getSuggestedSaleToken}, mas como função exportada.
 * Evita em dev/HMR o erro `contractService.getSuggestedSaleToken is not a function` quando o
 * bundle não recarrega o prototype da instância singleton.
 */
export async function fetchSuggestedSaleToken(
  clientId: string,
  options?: { deliveryCidade?: string; deliveryEstado?: string }
): Promise<string> {
  const api = (contractService as unknown as { api: AxiosInstance }).api;
  return requestSuggestedSaleToken(api, clientId, options);
}
